if (!customElements.get('eira-shipping-protection')) {
  class EiraShippingProtection extends HTMLElement {
    static busy = false;

    connectedCallback() {
      this.input = this.querySelector('input');
      this.input.checked = Number(this.dataset.quantity) > 0;
      this.onChoice = () => this.updateProtection(this.input.checked);
      this.input.addEventListener('change', this.onChoice);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.cartData?.items) this.syncFromCart(event.cartData);
        });
      }
      this.scheduleNormalization();
    }

    disconnectedCallback() {
      this.input?.removeEventListener('change', this.onChoice);
      this.unsubscribe?.();
      clearTimeout(this.normalizationTimer);
    }

    syncFromCart(cart) {
      const matching = cart.items.filter(item => String(item.variant_id || item.id) === this.dataset.variantId);
      const quantity = matching.reduce((sum, item) => sum + item.quantity, 0);
      const merchandise = cart.item_count - quantity;
      this.dataset.quantity = String(quantity);
      this.dataset.merchandiseQuantity = String(merchandise);
      this.input.checked = quantity > 0;
      this.hidden = merchandise === 0;
      this.scheduleNormalization();
    }

    scheduleNormalization() {
      clearTimeout(this.normalizationTimer);
      // Native cart events fire before their loading state is released.
      // Wait until that update finishes before removing an orphan fee or duplicate.
      this.normalizationTimer = setTimeout(() => {
        if (!this.isConnected || EiraShippingProtection.busy) return;
        const quantity = Number(this.dataset.quantity);
        const merchandise = Number(this.dataset.merchandiseQuantity);
        if (quantity > 0 && (merchandise === 0 || quantity > 1)) {
          this.updateProtection(merchandise > 0);
        }
      }, 0);
    }

    async updateProtection(selected) {
      if (EiraShippingProtection.busy || document.querySelector('.cart__items--disabled')) {
        this.input.checked = Number(this.dataset.quantity) > 0;
        return;
      }
      EiraShippingProtection.busy = true;
      const root = window.Shopify?.routes?.root || '/';
      const checkoutButtons = Array.from(document.querySelectorAll('button[name="checkout"]')).map(node => ({
        node, disabled: node.disabled
      }));
      checkoutButtons.forEach(({ node }) => { node.disabled = true; });
      document.querySelectorAll('eira-shipping-protection').forEach(node => {
        node.setAttribute('aria-busy', 'true');
        node.querySelector('input').disabled = true;
        node.querySelector('.eira-sp__error').hidden = true;
      });
      const drawer = document.querySelector('cart-drawer');
      const drawerWasOpen = drawer?.classList.contains('active');
      const sections = ['cart-drawer', 'cart-icon-bubble'];
      const pageItems = document.getElementById('main-cart-items');
      const pageFooter = document.getElementById('main-cart-footer');
      const itemsSection = pageItems?.dataset.id;
      const footerSection = pageFooter?.dataset.id;
      if (itemsSection) sections.push(itemsSection);
      if (footerSection) sections.push(footerSection);
      let cart;
      let didMutate = false;
      try {
        const currentResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        if (!currentResponse.ok) throw new Error('cart');
        const current = await currentResponse.json();
        const matching = current.items.filter(item => String(item.variant_id || item.id) === this.dataset.variantId);
        const merchandise = current.item_count - matching.reduce((sum, item) => sum + item.quantity, 0);
        const shouldAdd = selected && merchandise > 0;
        let endpoint = 'cart/update.js';
        const payload = { sections: [...new Set(sections)], sections_url: window.location.pathname };
        if (shouldAdd && matching.length === 0) {
          endpoint = 'cart/add.js';
          payload.items = [{ id: Number(this.dataset.variantId), quantity: 1, properties: { _eira_optional_shipping: 'true' } }];
        } else {
          payload.updates = {};
          matching.forEach((item, index) => { payload.updates[item.key] = shouldAdd && index === 0 ? 1 : 0; });
        }
        const response = await fetch(root + endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
        const state = await response.json();
        if (!response.ok || state.status >= 400 || state.errors) throw new Error('update');
        didMutate = true;
        const cartResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('cart');
        cart = await cartResponse.json();
        if (!state.sections?.['cart-drawer'] || !state.sections?.['cart-icon-bubble']) throw new Error('sections');
        const parse = html => new DOMParser().parseFromString(html, 'text/html');
        if (drawer) {
          const nextDrawer = parse(state.sections['cart-drawer']).querySelector('#CartDrawer');
          const existingDrawer = drawer.querySelector('#CartDrawer');
          if (!nextDrawer || !existingDrawer) throw new Error('drawer');
          existingDrawer.innerHTML = nextDrawer.innerHTML;
          drawer.classList.toggle('is-empty', cart.item_count === 0);
          drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
        }
        const bubble = document.getElementById('cart-icon-bubble');
        const nextBubble = parse(state.sections['cart-icon-bubble']).querySelector('.shopify-section');
        if (bubble && nextBubble) bubble.innerHTML = nextBubble.innerHTML;
        if (pageItems && itemsSection) {
          const nextItems = state.sections[itemsSection] && parse(state.sections[itemsSection]).querySelector('cart-items');
          if (!nextItems) throw new Error('items');
          pageItems.closest('cart-items').replaceWith(nextItems);
        }
        if (pageFooter && footerSection) {
          const nextFooter = state.sections[footerSection] && parse(state.sections[footerSection]).querySelector('#main-cart-footer');
          if (!nextFooter) throw new Error('footer');
          pageFooter.replaceWith(nextFooter);
        }
        if (drawerWasOpen) drawer?.open();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'eira-shipping-protection', cartData: cart });
        }
      } catch (error) {
        if (didMutate) {
          window.location.reload();
        } else if (this.isConnected) {
          this.input.checked = Number(this.dataset.quantity) > 0;
          const message = this.querySelector('.eira-sp__error');
          message.textContent = this.dataset.error;
          message.hidden = false;
        }
      } finally {
        EiraShippingProtection.busy = false;
        checkoutButtons.forEach(({ node, disabled }) => { if (node.isConnected) node.disabled = disabled; });
        document.querySelectorAll('eira-shipping-protection').forEach(node => {
          node.removeAttribute('aria-busy');
          node.querySelector('input').disabled = false;
          if (cart) node.syncFromCart(cart);
        });
      }
    }
  }
  customElements.define('eira-shipping-protection', EiraShippingProtection);
}

